import React from 'react';

import {Panel, Button, Icon, IconTypes, useSmUp} from '@pexip/components';

import type {
    BreakoutAskingForHelpStatus,
    BreakoutParticipants,
    RoomID,
} from '../../types';
import {BreakoutRoomVariant} from '../../types';
import {PanelHeader} from '../PanelHeader/PanelHeader.view';
import {BreakoutRoom} from '../BreakoutRoom/BreakoutRoom.view';
import {TestId} from '../../../test/testIds';
import {BreakoutRoomsAskingForHelp} from '../BreakoutRoomsAskingForHelp/BreakoutRoomsAskingForHelp.view';

import styles from './BreakoutPreviewPanel.module.scss';
import {useBreakoutCloseAllRoomsConfirmationModal} from '../../hooks/useBreakoutCloseAllRoomsConfirmationModal';

export const BreakoutPreviewPanel: React.FC<{
    breakoutsAskingForHelp: BreakoutAskingForHelpStatus[];
    currentParticipants: BreakoutParticipants;
    showCloseRoomsButton: boolean;
    closeRoomsText: string;
    editRoomsText: string;
    breakoutRoomsNames: Map<RoomID, string>;
    myIdentity?: string;
    amIControlOnlyParticipant: boolean;
    headerText?: string;
    onJoinRoom: (roomId: RoomID) => void;
    onJoinRoomViaHelp: (roomId: RoomID) => void;
    onCloseRoom: (roomId: RoomID) => void;
    onCloseRooms: () => void;
    onClosePanel: () => void;
    onEditRooms: () => void;
}> = ({
    breakoutsAskingForHelp,
    currentParticipants,
    showCloseRoomsButton,
    closeRoomsText,
    editRoomsText,
    breakoutRoomsNames,
    myIdentity,
    amIControlOnlyParticipant,
    headerText,
    onJoinRoom,
    onJoinRoomViaHelp,
    onCloseRoom,
    onCloseRooms,
    onClosePanel,
    onEditRooms,
}) => {
    const isSmUp = useSmUp();
    const {
        openBreakoutCloseAllRoomsConfirmationModal,
        breakoutCloseAllRoomsConfirmationModal,
    } = useBreakoutCloseAllRoomsConfirmationModal({onCloseRooms});

    return (
        <>
            <Panel
                isRounded={isSmUp}
                padding="none"
                footerPadding="small"
                headerContent={
                    <PanelHeader
                        title={headerText}
                        onCloseClick={onClosePanel}
                    />
                }
                footerContent={
                    showCloseRoomsButton ? (
                        <Button
                            onClick={openBreakoutCloseAllRoomsConfirmationModal}
                            modifier="fullWidth"
                            variant="danger"
                            data-testid={TestId.CloseBreakoutRooms}
                        >
                            {closeRoomsText}
                        </Button>
                    ) : undefined
                }
                scrollElementClassname="pt-2 pr-3 pb-3 pl-3"
            >
                <Button
                    className={styles.editButton}
                    modifier="fullWidth"
                    enhancerStart={
                        <Icon size="compact" source={IconTypes.IconEdit} />
                    }
                    onClick={onEditRooms}
                    variant="tertiary"
                    data-testid={TestId.ButtonBreakoutRoomEdit}
                >
                    {editRoomsText}
                </Button>
                <BreakoutRoomsAskingForHelp
                    breakoutsAskingForHelp={breakoutsAskingForHelp}
                    onJoinRoom={onJoinRoomViaHelp}
                />
                {Array.from(currentParticipants.entries()).map(
                    ([roomId, participants]) => {
                        return (
                            <BreakoutRoom
                                key={roomId}
                                name={breakoutRoomsNames.get(roomId) ?? roomId}
                                id={roomId}
                                participants={participants}
                                myIdentity={myIdentity}
                                amIControlOnlyParticipant={
                                    amIControlOnlyParticipant
                                }
                                variant={BreakoutRoomVariant.Preview}
                                onJoinRoom={onJoinRoom}
                                onCloseRoom={onCloseRoom}
                            />
                        );
                    },
                )}
            </Panel>
            {breakoutCloseAllRoomsConfirmationModal}
        </>
    );
};
